import React, { useMemo, useState } from 'react';
import { Booking } from '../types/booking';
import { BookingCard } from './BookingCard';
import { Search, Filter, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface BookingListProps {
  bookings: Booking[];
  onExtendBooking?: () => void;
}
export function BookingList({
  bookings,
  onExtendBooking
}: BookingListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [selectedFarm, setSelectedFarm] = useState<string>('all');
  // Parse date string (DD/MM/YYYY) to Date object
  const parseDate = (dateStr: string): Date => {
    const [day, month, year] = dateStr.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  };
  // Extract unique farms from bookings
  const uniqueFarms = useMemo(() => {
    const farms = bookings.map(b => b.farm || b.location).filter(f => f);
    return Array.from(new Set(farms)).sort();
  }, [bookings]);
  // Extract unique months from bookings
  const uniqueMonths = useMemo(() => {
    const months = bookings.map(b => {
      // Parse DD/MM/YYYY format
      const [day, month, year] = b.startDate.split('/');
      return `${year}-${month}`; // YYYY-MM format for sorting
    });
    return Array.from(new Set(months)).sort().reverse(); // Most recent first
  }, [bookings]);
  // Format month for display
  const formatMonth = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-AU', {
      month: 'long',
      year: 'numeric'
    });
  };
  // Count active filters
  const activeFilterCount = [selectedStatus !== 'all', selectedMonth !== 'all', selectedFarm !== 'all'].filter(Boolean).length;
  // Clear all filters
  const clearFilters = () => {
    setSelectedStatus('all');
    setSelectedMonth('all');
    setSelectedFarm('all');
  };
  // Filter and sort bookings
  const filteredAndSortedBookings = useMemo(() => {
    // First, filter the bookings
    const filtered = bookings.filter(booking => {
      // Search filter
      const matchesSearch = booking.machineName.toLowerCase().includes(searchQuery.toLowerCase()) || booking.userName.toLowerCase().includes(searchQuery.toLowerCase()) || booking.location.toLowerCase().includes(searchQuery.toLowerCase()) || booking.farm && booking.farm.toLowerCase().includes(searchQuery.toLowerCase());
      // Status filter
      const matchesStatus = selectedStatus === 'all' || booking.status === selectedStatus;
      // Month filter
      let matchesMonth = true;
      if (selectedMonth !== 'all') {
        const [day, month, year] = booking.startDate.split('/');
        const bookingMonth = `${year}-${month}`;
        matchesMonth = bookingMonth === selectedMonth;
      }
      // Farm filter
      const matchesFarm = selectedFarm === 'all' || booking.farm === selectedFarm || booking.location === selectedFarm;
      return matchesSearch && matchesStatus && matchesMonth && matchesFarm;
    });
    // Then, sort by date and status priority
    return filtered.sort((a, b) => {
      // Priority order: active > upcoming > completed > maintenance
      const statusPriority: Record<string, number> = {
        active: 0,
        upcoming: 1,
        completed: 2,
        maintenance: 3
      };
      const aPriority = statusPriority[a.status] ?? 999;
      const bPriority = statusPriority[b.status] ?? 999;
      // If same status, sort by start date (earliest first for active/upcoming, latest first for completed)
      if (aPriority === bPriority) {
        const aDate = parseDate(a.startDate);
        const bDate = parseDate(b.startDate);
        // For active and upcoming: earliest date first
        if (a.status === 'active' || a.status === 'upcoming') {
          return aDate.getTime() - bDate.getTime();
        }
        // For completed: latest date first (most recent completions at top)
        return bDate.getTime() - aDate.getTime();
      }
      return aPriority - bPriority;
    });
  }, [bookings, searchQuery, selectedStatus, selectedMonth, selectedFarm]);
  return <div className="space-y-6" data-id="element-128">
      {/* Controls Header */}
      <div className="flex items-center justify-end gap-2" data-id="element-129">
        <div className="relative flex-1 sm:w-64" data-id="element-130">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-131" />
          <input type="text" placeholder="Search bookings..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent" data-id="element-132" />
        </div>
        <div className="relative" data-id="element-133">
          <button onClick={() => setShowFilters(!showFilters)} className={`p-2 bg-white border rounded-lg transition-colors relative ${showFilters || activeFilterCount > 0 ? 'border-slate-900 text-slate-900' : 'border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-300'}`} data-id="element-134">
            <Filter size={18} data-id="element-135" />
            {activeFilterCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-slate-900 text-white text-xs rounded-full flex items-center justify-center font-bold" data-id="element-136">
                {activeFilterCount}
              </span>}
          </button>

          {/* Filter Dropdown */}
          <AnimatePresence data-id="element-137">
            {showFilters && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} className="absolute right-0 top-full mt-2 w-72 bg-white border border-slate-200 rounded-lg shadow-xl z-50" data-id="element-138">
                <div className="p-4 space-y-4" data-id="element-139">
                  {/* Header */}
                  <div className="flex items-center justify-between pb-3 border-b border-slate-100" data-id="element-140">
                    <h3 className="font-semibold text-slate-900" data-id="element-141">Filters</h3>
                    {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1" data-id="element-142">
                        <X size={12} data-id="element-143" />
                        Clear all
                      </button>}
                  </div>

                  {/* Status Filter */}
                  <div data-id="element-144">
                    <label className="block text-xs font-medium text-slate-700 mb-2" data-id="element-145">
                      Status
                    </label>
                    <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent" data-id="element-146">
                      <option value="all" data-id="element-147">All Statuses</option>
                      <option value="active" data-id="element-148">Active</option>
                      <option value="upcoming" data-id="element-149">Upcoming</option>
                      <option value="completed" data-id="element-150">Completed</option>
                      <option value="maintenance" data-id="element-151">Maintenance</option>
                    </select>
                  </div>

                  {/* Month Filter */}
                  <div data-id="element-152">
                    <label className="block text-xs font-medium text-slate-700 mb-2" data-id="element-153">
                      Month
                    </label>
                    <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent" data-id="element-154">
                      <option value="all" data-id="element-155">All Months</option>
                      {uniqueMonths.map(month => <option key={month} value={month} data-id="element-156">
                          {formatMonth(month)}
                        </option>)}
                    </select>
                  </div>

                  {/* Farm Filter */}
                  <div data-id="element-157">
                    <label className="block text-xs font-medium text-slate-700 mb-2" data-id="element-158">
                      Farm
                    </label>
                    <select value={selectedFarm} onChange={e => setSelectedFarm(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent" data-id="element-159">
                      <option value="all" data-id="element-160">All Farms</option>
                      {uniqueFarms.map(farm => <option key={farm} value={farm} data-id="element-161">
                          {farm}
                        </option>)}
                    </select>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Active Filters Display */}
      {activeFilterCount > 0 && <div className="flex items-center gap-2 flex-wrap" data-id="element-162">
          <span className="text-xs text-slate-500" data-id="element-163">Active filters:</span>
          {selectedStatus !== 'all' && <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs" data-id="element-164">
              Status: {selectedStatus}
              <button onClick={() => setSelectedStatus('all')} className="hover:text-slate-900" data-id="element-165">
                <X size={12} data-id="element-166" />
              </button>
            </span>}
          {selectedMonth !== 'all' && <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs" data-id="element-167">
              {formatMonth(selectedMonth)}
              <button onClick={() => setSelectedMonth('all')} className="hover:text-slate-900" data-id="element-168">
                <X size={12} data-id="element-169" />
              </button>
            </span>}
          {selectedFarm !== 'all' && <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs" data-id="element-170">
              {selectedFarm}
              <button onClick={() => setSelectedFarm('all')} className="hover:text-slate-900" data-id="element-171">
                <X size={12} data-id="element-172" />
              </button>
            </span>}
        </div>}

      {/* List Content */}
      <div className="space-y-3" data-id="element-173">
        <div className="flex items-center justify-between text-xs font-semibold text-slate-400 uppercase tracking-wider px-4" data-id="element-174">
          <span data-id="element-175">Farm / User</span>
          <span className="hidden sm:block" data-id="element-176">Schedule & Status</span>
        </div>

        <div className="space-y-3 min-h-[300px]" data-id="element-177">
          <AnimatePresence mode="popLayout" data-id="element-178">
            {filteredAndSortedBookings.length > 0 ? filteredAndSortedBookings.map((booking, index) => <BookingCard key={booking.id} booking={booking} index={index} onExtendBooking={onExtendBooking} data-id="element-179" />) : <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="text-center py-12 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-200" data-id="element-180">
                No bookings found matching your criteria.
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}